"use client";

import {JSX, useCallback, useRef} from "react";

import SmartImage from "@/shared/ui/smart-image";

import type {SliderTestimonialsProps} from "@/sections";
import {useKeenSlider} from "keen-slider/react";

import {useSliderNavigation} from "@/sections/content/slider-testimonials/hooks/use-slider-navigation";
import {useSliderState} from "@/sections/content/slider-testimonials/hooks/use-slider-state";
import {SliderTestimonialsNavigation} from "@/sections/content/slider-testimonials/parts/slider-testimonials-navigation";
import {SliderTestimonialsSlide} from "@/sections/content/slider-testimonials/parts/slider-testimonials-slide";
import {SliderTestimonialsTitle} from "@/sections/content/slider-testimonials/parts/slider-testimonials-title";
import {useSectionSpace} from "@/sections/use-section-space";

import {ContainerBase} from "@/shared/ui/layout/container-base";
import {assetPath} from "@/shared/utils/asset-path";

import "@/sections/content/slider-testimonials/styles/slider-testimonials.scss";
import "keen-slider/keen-slider.min.css";

export function SliderTestimonialsRoot({
    space,
    testimonials,
    children,
    title,
    titleSize,
    titleWeight,
    titleClassName,
    anchorId,
}: SliderTestimonialsProps): JSX.Element | null {
    const sectionVars = useSectionSpace(space);
    const sectionRef = useRef<HTMLElement | null>(null);

    const {currentIndex, sliderInstance, sliderConfig, handleSliderInit} = useSliderState(testimonials);
    const {handlePrev, handleNext} = useSliderNavigation(sliderInstance);

    const [sliderRef] = useKeenSlider<HTMLDivElement>(sliderConfig || undefined, [handleSliderInit]);

    const handleSlideClick = useCallback(
        (index: number): void => {
            if (index === currentIndex || testimonials.length <= 1) {
                return;
            }

            const total = testimonials.length;
            const prevIndex = (currentIndex - 1 + total) % total;
            const nextIndex = (currentIndex + 1) % total;

            if (index === prevIndex) {
                handlePrev();
            } else if (index === nextIndex) {
                handleNext();
            }
        },
        [currentIndex, testimonials.length, handlePrev, handleNext],
    );

    if (!testimonials || testimonials.length === 0) {
        return null;
    }

    const hasNavigation = testimonials.length > 1;

    return (
        <section ref={sectionRef} className="slider-testimonials" style={sectionVars} id={anchorId}>
            <ContainerBase>
                <div className="slider-testimonials__header" key="slider-testimonials-header">
                    {title && (
                        <SliderTestimonialsTitle key="slider-testimonials-title" size={titleSize} weight={titleWeight} className={titleClassName}>
                            {title}
                        </SliderTestimonialsTitle>
                    )}
                    {children && (
                        <div className="slider-testimonials__header-children" key="slider-testimonials-header-children">
                            {children}
                        </div>
                    )}
                    {hasNavigation && (
                        <div className="slider-testimonials__navigation-top" key="slider-testimonials-navigation">
                            <SliderTestimonialsNavigation
                                currentIndex={currentIndex}
                                totalTestimonials={testimonials.length}
                                hasNavigation={hasNavigation}
                                onPrev={handlePrev}
                                onNext={handleNext}
                            />
                        </div>
                    )}
                </div>
            </ContainerBase>
            <div ref={sliderRef} className="keen-slider slider-testimonials__slider">
                {testimonials.map((testimonial, index) => (
                    <SliderTestimonialsSlide
                        key={testimonial.id}
                        testimonial={testimonial}
                        index={index}
                        isActive={index === currentIndex}
                        onSlideClick={handleSlideClick}
                    />
                ))}
            </div>
            <ContainerBase>
                {hasNavigation && (
                    <div className="slider-testimonials__navigation-bottom" key="slider-testimonials-navigation">
                        <SliderTestimonialsNavigation
                            currentIndex={currentIndex}
                            totalTestimonials={testimonials.length}
                            hasNavigation={hasNavigation}
                            onPrev={handlePrev}
                            onNext={handleNext}
                        />
                    </div>
                )}
            </ContainerBase>
            <SmartImage
                src={assetPath("/images/decorations/wave-dec.svg")}
                alt=""
                width={640}
                height={548}
                className="slider-testimonials__decoration slider-testimonials__decoration--wave-dec"
                aria-hidden="true"
            />
            <SmartImage
                src={assetPath("/images/decorations/line-small.svg")}
                alt=""
                width={152}
                height={222}
                className="slider-testimonials__decoration slider-testimonials__decoration--line-small"
                aria-hidden="true"
            />
            <SmartImage
                src={assetPath("/images/decorations/wawe-big.svg")}
                alt=""
                width={139}
                height={419}
                className="slider-testimonials__decoration slider-testimonials__decoration--wave-big"
                aria-hidden="true"
            />
        </section>
    );
}

type SliderTestimonialsComponent = typeof SliderTestimonialsRoot & {
    Title: typeof SliderTestimonialsTitle;
};

export const SliderTestimonials = Object.assign(SliderTestimonialsRoot, {
    Title: SliderTestimonialsTitle,
}) as SliderTestimonialsComponent;
